import React, {useContext, useEffect, useRef} from "react";
import {createUseStyles, useTheme} from "react-jss";
import {useDimensionObserver} from "engage-client/hooks/useDimensionObserver";
import {
    axisBottom,
    axisLeft,
    curveMonotoneX,
    event as d3event,
    line,
    mouse as d3mouse,
    scaleLinear,
    select,
    Selection,
    ScaleLinear,
} from "d3";
import DisplayText, {TextVariant} from "engage-client/components/DisplayText";
import {isUndefined} from "engage-common/utils";
import clsx from "clsx";
import {Theme} from "engage-client/theme";
import {EngageAppContext} from "../../App";
import {TeamInfos} from "engage-client/components/teams/CreateTeams";

const TransparentWhite = "#FFFFFF44";
const TextGray = "#C7C7C7";


const useStyles = createUseStyles<Theme>((theme) => ({
    lineChart: {
        color: theme.accessoryPanel.dashBoard.sectionTitleColor,
        display: "flex",
        flexDirection: "column",
        background: theme.accessoryPanel.dashBoard.lineChart.background,
        justifyContent: "stretch",
        padding: 32,
        [theme.breakpoints.only("lg")] : {
            padding: 20
        },
        [theme.breakpoints.only("xs")] : {
            padding: 20
        },
    },
    yChartLabelContainer: {
        display:"flex",
        flex:1,
        alignItems: "stretch",
        color: TextGray,
    },
    yChartLabel: {
        writingMode: "vertical-rl",
        textAlign: "center",
        fontWeight: theme.accessoryPanel.dashBoard.simpleDashboard ? 700 : 400
    },
    svgContainer: {
        margin: [20,0,25,35],
        flex: 1,
    },
    svg: {
        overflow: "visible",
        background: theme.accessoryPanel.dashBoard.lineChart.svgBackground,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
    },
    title: {
        fontWeight: theme.accessoryPanel.dashBoard.simpleDashboard ? 700 : 400
    }
}));

export type LineChartData = {
    userID: string
    nickname: string
    totals: number[]
    currentPlayer: boolean
    teamID?:string
}

export type LineChartProps = {
    data: LineChartData[]
    totalQuestions: number
    className?: string
    hoveredUserID?: string
    host?: boolean
}

export default function LineChart({data, totalQuestions, className, hoveredUserID, host}:LineChartProps) {
    const {appState} = useContext(EngageAppContext.current);
    const theme = useTheme<Theme>();
    const svgRef = useRef<SVGSVGElement>(null);
    const svgWrapperRef = useRef<HTMLDivElement>(null);
    const {height, width} = useDimensionObserver(svgWrapperRef);

    useEffect(() => {
        const CurrentPlayer = theme.accessoryPanel.dashBoard.lineChart.currentPlayer.color;
        const HoverPlayer = theme.accessoryPanel.dashBoard.lineChart.hoverPlayer.color;

        const svg = select(svgRef.current);

        if(!height || !width || data.length === 0) { return; }

        let highScore = 0;
        for(const series of data) {
            highScore = Math.max(series.totals[series.totals.length-1], highScore);
        }
        const roundedHighScore = Math.ceil(highScore/100)*100 + 200;

        const xScale = scaleLinear()
            .domain([0,totalQuestions])
            .range([0, width]);

        const yScale = scaleLinear()
            .domain([0, roundedHighScore])
            .range([height, 0]);

        const xAxis = axisBottom(xScale).ticks(totalQuestions);
        svg.select<SVGGElement>(".x-axis")
            .style("transform", `translateY(${height}px)`)
            .call(xAxis.tickSizeOuter(0))
            .selectAll("line").attr("stroke-width",0);
        svg.select<SVGGElement>(".gridlines")
            .attr("transform", "translate(0," + height + ")")
            .attr("color", TransparentWhite)
            .attr( "fill", "none")
            .attr("stroke-dasharray", "2,2")
            .call(xAxis.tickSize(-height).tickFormat(() => ""));

        const yAxis = axisLeft(yScale).ticks(roundedHighScore / 100);
        svg.select<SVGGElement>(".y-axis")
            .call(yAxis.tickSizeOuter(0))
            .selectAll("line").attr("stroke-width",0);

        const myLine = line<number>()
            .defined(d => !isNaN(d))
            .x((value, index) => xScale(index) ?? 0)
            .y((value) => yScale(value) ?? 0)
            .curve(curveMonotoneX);

        const lines = svg.select<SVGGElement>(".lines").selectAll(".line")
            .data(data)
            .join("path")
            .attr("class", "line")
            .attr("d", d => myLine(d.totals))
            .attr("fill", "none")
            .attr("stroke", d=>lineColor(d, theme.teamInfos, CurrentPlayer))
            .attr("stroke-width", d=>lineWidth(d, theme.teamInfos))
            .attr("stroke-linecap", "round")
            .style("pointer-events", "all");

        addHover(svg, lines, xScale, yScale, data, totalQuestions, CurrentPlayer, HoverPlayer, theme.teamInfos, hoveredUserID);

    }, [svgRef, width, height, data, totalQuestions, hoveredUserID]);

    const classes = useStyles();
    const hostTitle = appState.extra.participantCount > 10 ? "TOP 10" : "PLAYER PROGRESS";
    return (
        <div className={clsx(classes.lineChart,className)}>
            <DisplayText variant={TextVariant.s18reg} className={classes.title}>{host ? hostTitle : "YOU VS. THE LEADERS"}</DisplayText>
            <div className={classes.yChartLabelContainer}>
                <DisplayText variant={TextVariant.s12reg} className={classes.yChartLabel}>SCORE</DisplayText>
                <div ref={svgWrapperRef} className={classes.svgContainer}>
                    <svg ref={svgRef} className={classes.svg}>
                        <g className="gridlines" />
                        <g className="lines" />
                        <g className="x-axis" />
                        <g className="y-axis" />
                        <g className="dot" />
                    </svg>
                </div>
            </div>

            <DisplayText variant={TextVariant.s12reg} color={TextGray} align="center" className={classes.title}>QUESTIONS</DisplayText>
        </div>
    );
}

function lineColor(d:LineChartData, teamInfos: TeamInfos, CurrentPlayer: string) {
    if(d.currentPlayer) {
        if (d.teamID) {
            return "white";
        }
        return CurrentPlayer;
    } else if(teamInfos[d.userID]) {
        return teamInfos[d.userID].lightBG;
    } else {
        return TransparentWhite;
    }
}

function highlightLineColor(d:LineChartData, s:LineChartData, CurrentPlayer: string, HoverPlayer: string, teamInfos: TeamInfos) {
    if(d.currentPlayer) {
        return CurrentPlayer;
    } else if(d !== s) {
        return TransparentWhite;
    } else if(teamInfos[d.userID]) {
        return teamInfos[d.userID].main;
    } else if(d.teamID && teamInfos[d.teamID]) {
        return teamInfos[d.teamID].main;
    } else {
        return HoverPlayer;
    }
}

function lineWidth(d:LineChartData, teamInfos: TeamInfos) {
    if(d.currentPlayer) {
        return 2.5;
    } else if(teamInfos[d.userID]) {
        return 2;
    } else {
        return 1.5;
    }
}

function addHover(svg: Selection<any, unknown, null, undefined>,
                  path: Selection<any, LineChartData, SVGGElement, unknown>,
                  xScale: ScaleLinear<number, number>,
                  yScale: ScaleLinear<number, number>,
                  data: LineChartData[],
                  totalQuestions: number,
                  CurrentPlayer: string,
                  HoverPlayer: string,
                  teamInfos: TeamInfos,
                  hoveredUserID?:string) {

    function moved(this: SVGSVGElement) {
        d3event.preventDefault();
        const mouse = d3mouse(this);
        const xm = xScale.invert(mouse[0]);
        const ym = yScale.invert(mouse[1]);
        const i = Math.round(xm);
        const s = data.reduce((p, c) => {
            if(Math.abs(c.totals[i] - ym) < Math.abs(p.totals[i] - ym)) {
                return c;
            }
            return p;
        });
        highlightPlayer(s, i, CurrentPlayer, HoverPlayer, teamInfos);
    }
    function left() {
        path.attr("stroke", d=>lineColor(d,teamInfos,CurrentPlayer));
        dot.attr("display", "none");
    }


    if ("ontouchstart" in document) {
        svg.style("-webkit-tap-highlight-color", "transparent")
            .on("touchmove", moved)
            .on("touchend", left);
    } else {
        svg.on("mousemove", moved)
            .on("mouseleave", left);
    }

    const dot = svg.select(".dot")
        .attr("display", "none").attr("fill", "white");

    dot.append("circle")
        .attr("r", 2.5);

    dot.append("text")
        .attr("cursor", "default")
        .attr("font-family", "Roboto")
        .attr("font-size", 14)
        .attr("y", -8);

    if(hoveredUserID) {
        let userData = data.find(d => d.userID === hoveredUserID);
        if(userData) {
            highlightPlayer(userData, userData.totals.length-1, CurrentPlayer, HoverPlayer, teamInfos);
        }
    }

    function highlightPlayer(s:LineChartData, i:number, c: string, h: string, teamInfos: TeamInfos) {
        path.attr("stroke", d=>highlightLineColor(d,s,c,h, teamInfos)).filter(d => d === s).raise();
        if(!isUndefined(s.totals[i])) {
            dot.attr("transform", `translate(${xScale(i)},${yScale(s.totals[i])})`);
            dot.select("text").text(`${s.nickname}: ${s.totals[i].toFixed(0)}`);
            dot.attr("display", null);
            dot.attr("text-anchor", i === totalQuestions ? "end":"middle");
        }
    }
}